import { useState, useEffect, useRef, useCallback } from 'react'
import type { AnimationKeyframe, CameraState } from '@/types/camera'
import { quaternionSlerp, IDENTITY_QUATERNION } from './quaternion'
import { toQuaternionCamera } from './cameraConversion'

/**
 * Get camera state at a specific time using proper interpolation
 */
export function getCameraAtTime(keyframes: AnimationKeyframe[], time: number): CameraState {
  if (keyframes.length === 0) {
    return {
      position: { x: 0, y: 0, z: 1 },
      quaternion: IDENTITY_QUATERNION
    }
  }

  if (keyframes.length === 1) {
    return keyframes[0].camera
  }

  // Find the two keyframes to interpolate between
  let prevKeyframe = keyframes[0]
  let nextKeyframe = keyframes[keyframes.length - 1]

  for (let i = 0; i < keyframes.length - 1; i++) {
    if (keyframes[i].time <= time && keyframes[i + 1].time >= time) {
      prevKeyframe = keyframes[i]
      nextKeyframe = keyframes[i + 1]
      break
    }
  }

  // If time is before first keyframe
  if (time <= keyframes[0].time) {
    return keyframes[0].camera
  }

  // If time is after last keyframe
  if (time >= keyframes[keyframes.length - 1].time) {
    return keyframes[keyframes.length - 1].camera
  }

  // Interpolation factor
  const t = (time - prevKeyframe.time) / (nextKeyframe.time - prevKeyframe.time)

  // Convert both cameras to quaternion format
  const prevQuatCamera = toQuaternionCamera(prevKeyframe.camera)
  const nextQuatCamera = toQuaternionCamera(nextKeyframe.camera)

  // Interpolate position linearly
  const position = {
    x: prevQuatCamera.position.x + (nextQuatCamera.position.x - prevQuatCamera.position.x) * t,
    y: prevQuatCamera.position.y + (nextQuatCamera.position.y - prevQuatCamera.position.y) * t,
    z: prevQuatCamera.position.z + (nextQuatCamera.position.z - prevQuatCamera.position.z) * t
  }

  // Interpolate rotation using SLERP for smooth rotation
  const quaternion = quaternionSlerp(prevQuatCamera.quaternion, nextQuatCamera.quaternion, t)

  return { position, quaternion }
}

/**
 * Hook for managing camera animation playback
 */
export function useCameraAnimation(
  keyframes: AnimationKeyframe[],
  duration: number,
  loop: boolean = false,
  enabled: boolean = true
) {
  const [currentTime, setCurrentTime] = useState(0)
  const [isPlaying, setIsPlaying] = useState(enabled)
  const animationFrameRef = useRef<number | undefined>(undefined)
  const startTimeRef = useRef<number | undefined>(undefined)

  const currentCamera = getCameraAtTime(keyframes, currentTime)

  const play = useCallback(() => {
    setIsPlaying(true)
    startTimeRef.current = undefined
  }, [])

  const pause = useCallback(() => {
    setIsPlaying(false)
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current)
    }
  }, [])

  const reset = useCallback(() => {
    setCurrentTime(0)
    startTimeRef.current = undefined
  }, [])

  const seek = useCallback((time: number) => {
    setCurrentTime(Math.max(0, Math.min(time, duration)))
  }, [duration])

  useEffect(() => {
    if (!isPlaying || !enabled || keyframes.length === 0) {
      return
    }

    const animate = (timestamp: number) => {
      if (!startTimeRef.current) {
        startTimeRef.current = timestamp - currentTime * 1000
      }

      const elapsed = (timestamp - startTimeRef.current) / 1000
      
      if (elapsed >= duration) {
        if (loop) {
          startTimeRef.current = timestamp
          setCurrentTime(0)
        } else {
          setCurrentTime(duration)
          setIsPlaying(false)
          return
        }
      } else {
        setCurrentTime(elapsed)
      }

      animationFrameRef.current = requestAnimationFrame(animate)
    }

    animationFrameRef.current = requestAnimationFrame(animate)

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [isPlaying, enabled, keyframes, duration, loop, currentTime])

  return {
    currentCamera,
    currentTime,
    isPlaying,
    play,
    pause,
    reset,
    seek
  }
}

// Re-export conversion functions from the new module for backward compatibility
export { cameraStateToArrays, arraysToCameraState } from './cameraConversion'